// EmailVerification.js
import React, { useState, useEffect } from "react";
import { Button, Spin, Result } from "antd";
import {
  CheckCircle,
  XCircle,
  Mail,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

const EmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState("verifying"); // verifying, success, error, already_verified
  const [countdown, setCountdown] = useState(5);

  // API hook for email verification
  const [verifyResponse, verifyEmail] = useApi();

  useEffect(() => {
    if (token) {
      // Call verification API
      verifyEmail(null, API_METHODS.GET, `/user/auth/verify-email/${token}`);
    } else {
      setVerificationStatus("error");
    }
  }, [token]);

  // Handle API response
  useEffect(() => {
    if (verifyResponse.data && !verifyResponse.isLoading) {
      if (verifyResponse.data.user?.already_verified) {
        setVerificationStatus("already_verified");
      } else {
        setVerificationStatus("success");
      }

      // Start countdown for auto-redirect
      startCountdown();
    } else if (verifyResponse.error) {
      setVerificationStatus("error");
    }
  }, [verifyResponse]);

  const startCountdown = () => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/login");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleLoginRedirect = () => {
    navigate("/login");
  };

//   const handleResendVerification = () => {
//     // This would require another API endpoint to resend verification email
//     alert("Please contact support to resend verification email.");
//   };

  const renderContent = () => {
    switch (verificationStatus) {
      case "verifying":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Spin size="large" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Verifying Your Email
            </h2>
            <p className="text-gray-600 mb-6">
              Please wait while we verify your email address...
            </p>
            <div className="flex items-center justify-center space-x-2 text-orange-600">
              <Mail className="h-5 w-5 animate-pulse" />
              <span className="text-sm">Processing verification</span>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              🎉 Email Verified Successfully!
            </h2>
            <p className="text-gray-600 mb-6">
              Welcome to MahalaxmiPuja! Your email has been verified and your
              account is now active.
            </p>

            {verifyResponse.data?.user && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <h3 className="font-semibold text-green-800 mb-2">
                  Account Details:
                </h3>
                <p className="text-sm text-green-700">
                  <strong>Name:</strong> {verifyResponse.data.user.name}
                </p>
                <p className="text-sm text-green-700">
                  <strong>Email:</strong> {verifyResponse.data.user.email}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-blue-700 text-sm mb-2">
                  Redirecting to login in <strong>{countdown}</strong>{" "}
                  seconds...
                </p>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                  ></div>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                onClick={handleLoginRedirect}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                icon={<ArrowRight className="h-4 w-4" />}
              >
                Continue to Login
              </Button>
            </div>
          </div>
        );

      case "already_verified":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Email Already Verified
            </h2>
            <p className="text-gray-600 mb-6">
              Your email address is already verified. You can login to your
              account.
            </p>

            {verifyResponse.data?.user && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <h3 className="font-semibold text-blue-800 mb-2">
                  Account Details:
                </h3>
                <p className="text-sm text-blue-700">
                  <strong>Name:</strong> {verifyResponse.data.user.name}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Email:</strong> {verifyResponse.data.user.email}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                <p className="text-blue-700 text-sm mb-2">
                  Redirecting to login in <strong>{countdown}</strong>{" "}
                  seconds...
                </p>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                  ></div>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                onClick={handleLoginRedirect}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600"
                icon={<ArrowRight className="h-4 w-4" />}
              >
                Continue to Login
              </Button>
            </div>
          </div>
        );

      case "error":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Verification Failed
            </h2>
            <p className="text-gray-600 mb-6">
              {verifyResponse.error ||
                "The verification link is invalid or has expired."}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
              <h3 className="font-semibold text-red-800 mb-2">
                What can you do?
              </h3>
              <ul className="text-sm text-red-700 space-y-1 text-left">
                <li>• Check if you clicked the correct link from your email</li>
                <li>• The verification link may have expired</li>
                <li>• Try signing up again if needed</li>
                <li>• Contact support for assistance</li>
              </ul>
            </div>

            <div className="space-y-3">
              {/* <Button
                size="large"
                onClick={handleResendVerification}
                icon={<RefreshCw className="h-4 w-4" />}
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                Request New Verification
              </Button> */}

              <div className="text-center">
                <Button
                  type="link"
                  onClick={() => navigate("/sign-up")}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Back to Sign Up
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <HeaderComponent />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="container mx-auto max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-xl">
                🪔
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  MahalaxmiPuja
                </h1>
                <p className="text-orange-600 font-medium text-sm">
                  Email Verification
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-orange-100 overflow-hidden">
            {renderContent()}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EmailVerification;
